import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { MyDues } from '../components/MyDues';
import { AvatarUpload } from '../components/AvatarUpload';
import { ExportPanel } from '../components/ExportPanel';
import { useMutation, useQuery } from '@tanstack/react-query';
import { can } from '@gwc/shared';
import { useAuth } from '../auth/AuthProvider';
import {
  useNotificationPreferences,
  MESSAGE_CATEGORIES,
  MESSAGE_CATEGORY_LABELS,
} from '../hooks/useNotificationPreferences';
import type { MessageCategory } from '@gwc/shared';
import { notificationsApi } from '../api/notifications';
import { authApi } from '../api/auth';
import { membersApi } from '../api/members';
import { billingApi } from '../api/billing';
import { ApiError } from '../lib/api';


const DESCRIPTIONS: Record<MessageCategory, string> = {
  announcements: 'New tastings and messages from club leadership.',
  eventReminders: "Nudges to RSVP before an upcoming tasting.",
  rsvpUpdates: 'Confirmation, waitlist, and cancellation updates.',
  liveTasting: 'Alerts when a tasting you registered for goes live.',
  dues: 'Charges, reminders, and payment receipts.',
};

const money = (n: number) => n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

export function Account() {
  const { user, refreshUser } = useAuth();
  const { preferences, loading, toggle, saving } = useNotificationPreferences();
  const canAnnounce = user ? can(user.roles, 'members:manage') : false;
  const [firstName, setFirstName] = useState(user?.firstName ?? '');
  const [lastName, setLastName] = useState(user?.lastName ?? '');
  const [phone, setPhone] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');


  const invoicesQuery = useQuery({
    queryKey: ['billing', 'invoices', user?.id],
    queryFn: () => billingApi.listInvoices({ personId: user!.id }),
    enabled: Boolean(user?.id),
  });


  const checkoutMut = useMutation({
    mutationFn: (chargeId: string) => billingApi.checkout({ chargeId, provider: 'stripe' }),
  });

  const profileMut = useMutation({
    mutationFn: () =>
      authApi.updateProfile({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        phone: phone.trim() || null,
      }),
    onSuccess: async () => {
      await refreshUser();
    },
  });

  const passwordMut = useMutation({
    mutationFn: () => authApi.changePassword(currentPassword, newPassword),
    onSuccess: () => {
      setCurrentPassword('');
      setNewPassword('');
    },
  });


  return (
    <div className="mx-auto max-w-lg space-y-8">
      <div>
        <h1 className="font-display text-3xl text-cream-50">My account</h1>
        <p className="mt-2 text-sm text-cream-300/70">
          Notification settings sync with the mobile app — change them here or on your phone.
        </p>
      </div>

      <section className="card space-y-4 p-6">
        <h2 className="font-display text-xl text-cream-50">Profile</h2>
        <div className="mt-4">
          <AvatarUpload />
        </div>
        <p className="text-sm text-cream-300/70">{user?.email}</p>
        <form
          className="space-y-3"
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            profileMut.mutate();
          }}
        >
          <div className="grid gap-3 sm:grid-cols-2">
            <input className="field" value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="First name" />
            <input className="field" value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Last name" />
          </div>
          <input className="field" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Phone (optional)" />
          <button type="submit" className="btn-primary text-sm" disabled={profileMut.isPending}>
            {profileMut.isPending ? 'Saving…' : 'Save profile'}
          </button>
          {profileMut.isError && (
            <p className="text-sm text-wine-300">
              {profileMut.error instanceof ApiError ? profileMut.error.message : 'Could not save profile.'}
            </p>
          )}
        </form>
      </section>

      <section className="card space-y-4 p-6">
        <h2 className="font-display text-xl text-cream-50">Password</h2>
        <form
          className="space-y-3"
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            passwordMut.mutate();
          }}
        >
          <input
            type="password"
            className="field"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            placeholder="Current password"
            autoComplete="current-password"
          />
          <input
            type="password"
            className="field"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder="New password (10+ characters)"
            autoComplete="new-password"
            minLength={10}
          />
          <button type="submit" className="btn-primary text-sm" disabled={passwordMut.isPending}>
            {passwordMut.isPending ? 'Updating…' : 'Change password'}
          </button>
          {passwordMut.isSuccess && <p className="text-sm text-gold-200">Password updated.</p>}
          {passwordMut.isError && (
            <p className="text-sm text-wine-300">
              {passwordMut.error instanceof ApiError ? passwordMut.error.message : 'Could not change password.'}
            </p>
          )}
        </form>
        <Link to="/forgot-password" className="text-sm text-gold-200 hover:underline">
          Forgot your password?
        </Link>
      </section>


      <MyDues />

      <ExportPanel />

      <section className="card p-6">
        <h2 className="font-display text-xl text-cream-50">Invoices</h2>
        {invoicesQuery.isLoading ? (
          <p className="mt-3 text-sm text-cream-300/50">Loading…</p>
        ) : (invoicesQuery.data?.data.length ?? 0) === 0 ? (
          <p className="mt-3 text-sm text-cream-300/50">No invoices yet.</p>
        ) : (
          <ul className="mt-3 divide-y divide-ink-800">
            {invoicesQuery.data!.data.map((inv) => (
              <li key={inv.id} className="py-3 text-sm">
                <Link to={`/billing/invoices/${inv.id}`} className="text-gold-200 hover:underline">
                  {inv.invoiceNumber}
                </Link>
                <p className="text-xs text-cream-300/50">
                  {money(Number(inv.subtotal))} · <span className="capitalize">{inv.status}</span>
                </p>
              </li>
            ))}
          </ul>
        )}
      </section>


      <section className="card p-6">
        <h2 className="font-display text-xl text-cream-50">Notifications</h2>
        <p className="mt-1 text-sm text-cream-300/70">
          Choose how the club reaches you. Whatever you switch off here, every message still
          arrives in your inbox — this only controls whether we also buzz your phone or email you.
        </p>

        <div className="mt-5 overflow-x-auto">
          <table className="w-full min-w-[420px] text-left">
            <thead>
              <tr className="border-b border-ink-800 text-xs uppercase tracking-wide text-cream-300/50">
                <th className="pb-2 font-normal">Notify me about</th>
                <th className="w-20 pb-2 text-center font-normal">Push</th>
                <th className="w-20 pb-2 text-center font-normal">Email</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-800">
              {loading && !preferences ? (
                <tr>
                  <td colSpan={3} className="py-4 text-sm text-cream-300/60">
                    Loading preferences…
                  </td>
                </tr>
              ) : (
                MESSAGE_CATEGORIES.map((key) => (
                  <tr key={key}>
                    <td className="py-3 pr-4">
                      <span className="block text-sm text-cream-100">
                        {MESSAGE_CATEGORY_LABELS[key]}
                      </span>
                      <span className="mt-0.5 block text-xs text-cream-300/60">
                        {DESCRIPTIONS[key]}
                      </span>
                    </td>
                    {(['push', 'email'] as const).map((channel) => (
                      <td key={channel} className="py-3 text-center">
                        <input
                          type="checkbox"
                          className="h-4 w-4 accent-gold-500"
                          aria-label={`${MESSAGE_CATEGORY_LABELS[key]} — ${channel}`}
                          checked={preferences?.[key]?.[channel] ?? true}
                          disabled={saving}
                          onChange={() => toggle(key, channel)}
                        />
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {canAnnounce ? (
        <section className="card space-y-3 p-6">
          <h2 className="font-display text-xl text-cream-50">Club communications</h2>
          <p className="text-sm text-cream-300/70">
            Compose a message to the membership — choose an audience, send by push and email,
            preview it, and see exactly who received it.
          </p>
          <Link to="/comms" className="btn-primary inline-block">
            Open the composer
          </Link>
        </section>
      ) : null}
    </div>
  );
}
